import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BadgePercent,
  Bell,
  BriefcaseBusiness,
  CalendarDays,
  Camera,
  CheckCircle2,
  Clock3,
  FileText,
  Filter,
  Grid3X3,
  KeyRound,
  Link2,
  List,
  Loader2,
  MapPin,
  MoreVertical,
  PenLine,
  Plus,
  Search,
  ShieldCheck,
  Smartphone,
  Trash2,
  UploadCloud,
  User,
  X,
} from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_BASE, AUTH_AUDIENCE } from '../../config/env';
import { languageLabels, supportedLanguages } from '../../i18n/LanguageContext';
import { useI18n } from '../../i18n/LanguageContext';
import type { SupportedLanguage } from '../../i18n/translations';

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  role: { name: string };
  title?: string;
  phone?: string;
  active: boolean;
  status?: 'ACTIVE' | 'ARCHIVED';
  mustChangePassword?: boolean;
  secondaryRoles?: string[];
  linkedProfileType?: 'PROVIDER' | 'STAFF' | null;
  linkedProviderProfileId?: string | null;
  linkedProviderType?: string | null;
  linkedStaffProfileId?: string | null;
  linkedStaffRole?: string | null;
  timesheetEnabled?: boolean;
}

interface Role {
  id: number;
  name: string;
}

type MePayload = {
  role?: string;
  roles?: string[];
};

type Notice = {
  type: 'success' | 'error' | 'info';
  message: string;
};

type ProviderProfileOption = {
  id: string;
  firstName: string;
  lastName: string;
  type: string;
  userId?: string | null;
};

type StaffProfileOption = {
  id: string;
  firstName: string;
  lastName: string;
  role: string;
  userId?: string | null;
};

type LocationOption = {
  id: string;
  name: string;
  addressLine1?: string | null;
  city?: string | null;
  isPrimary?: boolean;
};

type ViewMode = 'grid' | 'list';
type TeamTab = 'personal' | 'services' | 'permissions' | 'locations' | 'timesheets' | 'leave' | 'documents';

const showPrefill = import.meta.env.DEV;
const fallbackLocations: LocationOption[] = [
  { id: 'elen1-main', name: 'Elen1 Zacatecas', addressLine1: 'Primary clinic', isPrimary: true },
  { id: 'remote-care', name: 'Remote care', addressLine1: 'Virtual visits' },
  { id: 'front-desk', name: 'Front desk', addressLine1: 'Reception operations' },
];

export default function SettingsUsers() {
  const navigate = useNavigate();
  const { userId } = useParams();
  const { getAccessTokenSilently } = useAuth0();
  const { t } = useI18n();

  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState<Role[]>([]);
  const [providerProfiles, setProviderProfiles] = useState<ProviderProfileOption[]>([]);
  const [staffProfiles, setStaffProfiles] = useState<StaffProfileOption[]>([]);
  const [locations, setLocations] = useState<LocationOption[]>(fallbackLocations);
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('all');
  const [adminOnlyFilter, setAdminOnlyFilter] = useState(false);
  const [createStep, setCreateStep] = useState<'details' | 'permissions'>('details');
  const [detailTab, setDetailTab] = useState<TeamTab>('personal');
  const [openActionMenu, setOpenActionMenu] = useState(false);
  const [showCustomizeFields, setShowCustomizeFields] = useState(false);
  const [prescriberFields, setPrescriberFields] = useState({
    prescriptionInfo: '',
    registrationNumber: '',
    dea: '',
    npi: '',
    cdPin: '',
    qCode: '',
  });

  const [roleLoading, setRoleLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [savingCreate, setSavingCreate] = useState(false);
  const [showResetDialogFor, setShowResetDialogFor] = useState<User | null>(null);
  const [processingReset, setProcessingReset] = useState(false);
  const [deletingUserId, setDeletingUserId] = useState<string | null>(null);
  const [detailUser, setDetailUser] = useState<User | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const [formData, setFormData] = useState({
    email: '',
    firstName: '',
    lastName: '',
    roleId: 0,
    preferredLanguage: '' as SupportedLanguage | '',
    secondaryRoles: [] as string[],
    title: '',
    phone: '',
    timesheetEnabled: false,
    defaultLocationId: '',
    assignedLocationIds: [] as string[],
    canAccessAllLocations: false,
  });

  const [editFormData, setEditFormData] = useState({
    firstName: '',
    lastName: '',
    roleId: 0,
    secondaryRoles: [] as string[],
    title: '',
    phone: '',
    active: true,
    timesheetEnabled: false,
  });
  const [associationForm, setAssociationForm] = useState({
    profileType: 'NONE' as 'NONE' | 'PROVIDER' | 'STAFF',
    providerProfileId: '',
    staffProfileId: '',
    providerType: '',
    staffRole: '',
  });

  useEffect(() => {
    let active = true;
    void (async () => {
      setRoleLoading(true);
      try {
        const token = await getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
        const res = await fetch(`${API_BASE}/users/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(t('Unable to load current user role.'));
        const me = (await res.json()) as MePayload;
        const roles = (Array.isArray(me.roles) ? me.roles : [me.role]).map((entry) => String(entry || '').toUpperCase());
        const admin = roles.includes('ADMIN');
        if (!active) return;
        setIsAdmin(admin);
        if (!admin) {
          setNotice({ type: 'error', message: t('Only administrators can access Team.') });
          return;
        }
        await Promise.all([loadRoles(), loadUsers(), loadProfileDirectories(), loadLocations()]);
      } catch (err) {
        if (!active) return;
        setNotice({ type: 'error', message: (err as Error).message || t('Unable to load user management.') });
      } finally {
        if (active) setRoleLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!userId) {
      setDetailUser(null);
      return;
    }
    const matched = users.find((user) => user.id === userId) || null;
    setDetailUser(matched);
    if (matched) {
      const matchedRole = roles.find((role) => role.name === matched.role.name);
      setEditFormData({
        firstName: matched.firstName || '',
        lastName: matched.lastName || '',
        roleId: matchedRole?.id ?? roles[0]?.id ?? 0,
        secondaryRoles: Array.isArray(matched.secondaryRoles) ? matched.secondaryRoles : [],
        title: matched.title || '',
        phone: matched.phone || '',
        active: Boolean(matched.active),
        timesheetEnabled: Boolean(matched.timesheetEnabled),
      });
      setAssociationForm({
        profileType: matched.linkedProfileType ?? 'NONE',
        providerProfileId: matched.linkedProviderProfileId ?? '',
        staffProfileId: matched.linkedStaffProfileId ?? '',
        providerType: matched.linkedProviderType ?? '',
        staffRole: matched.linkedStaffRole ?? '',
      });
      setPrescriberFields({
        prescriptionInfo: `${matched.title || matched.role.name} credentials for prescriptions`,
        registrationNumber: matched.linkedProviderType ? 'GMC-1234567' : '',
        dea: '',
        npi: '',
        cdPin: '',
        qCode: '',
      });
    }
  }, [userId, users, roles]);

  async function getToken() {
    return getAccessTokenSilently({ authorizationParams: { audience: AUTH_AUDIENCE } });
  }

  async function readErrorMessage(res: Response, fallback: string) {
    const payload = await res.json().catch(() => ({}));
    const msg = typeof payload?.message === 'string' ? payload.message : fallback;
    return msg;
  }

  async function loadUsers() {
    setLoading(true);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, t('Unable to load users.')));
      }
      setUsers(await res.json());
    } finally {
      setLoading(false);
    }
  }

  async function loadRoles() {
    const token = await getToken();
    const res = await fetch(`${API_BASE}/roles`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      throw new Error(await readErrorMessage(res, t('Unable to load roles.')));
    }
    const data: Role[] = await res.json();
    setRoles(data);
    if (data.length && formData.roleId === 0) {
      setFormData((prev) => ({ ...prev, roleId: data[0].id }));
    }
  }

  async function loadProfileDirectories() {
    const token = await getToken();
    const [providerRes, staffRes] = await Promise.all([
      fetch(`${API_BASE}/providers`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      fetch(`${API_BASE}/staff`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);
    if (!providerRes.ok) {
      throw new Error(await readErrorMessage(providerRes, t('Unable to load provider profiles.')));
    }
    if (!staffRes.ok) {
      throw new Error(await readErrorMessage(staffRes, t('Unable to load staff profiles.')));
    }
    setProviderProfiles(await providerRes.json());
    setStaffProfiles(await staffRes.json());
  }

  async function loadLocations() {
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as LocationOption[];
      if (Array.isArray(data) && data.length) {
        setLocations(data);
        setFormData((prev) => ({
          ...prev,
          defaultLocationId: prev.defaultLocationId || data.find((location) => location.isPrimary)?.id || data[0].id,
        }));
      }
    } catch {
      setLocations(fallbackLocations);
    }
  }

  const prefill = () => {
    const defaultRoleId = roles[0]?.id ?? formData.roleId;
    setFormData({
      email: 'dana.nguyen@example.com',
      firstName: 'Dana',
      lastName: 'Nguyen',
      roleId: defaultRoleId,
      preferredLanguage: 'en',
      secondaryRoles: [],
      title: 'Operations Manager',
      phone: '555-234-7788',
      timesheetEnabled: true,
      defaultLocationId: locations[0]?.id || '',
      assignedLocationIds: locations.slice(1, 2).map((location) => location.id),
      canAccessAllLocations: false,
    });
  };

  async function handleCreateSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!formData.roleId) {
      setNotice({ type: 'error', message: t('Please select a role.') });
      return;
    }

    setSavingCreate(true);
    setNotice(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          ...formData,
          preferredLanguage: formData.preferredLanguage || null,
        }),
      });
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, t('Failed to create user.')));
      }

      setShowCreateModal(false);
      setFormData({
        email: '',
        firstName: '',
        lastName: '',
        roleId: roles[0]?.id ?? 0,
        preferredLanguage: '',
        secondaryRoles: [],
        title: '',
          phone: '',
          timesheetEnabled: false,
          defaultLocationId: locations[0]?.id || '',
          assignedLocationIds: [],
          canAccessAllLocations: false,
        });
      await loadUsers();
      setNotice({ type: 'success', message: t('User created. Temporary password was sent by email.') });
    } catch (err) {
      setNotice({ type: 'error', message: (err as Error).message || t('Failed to create user.') });
    } finally {
      setSavingCreate(false);
    }
  }

  async function handleEditSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!detailUser) return;
    if (!editFormData.roleId) {
      setNotice({ type: 'error', message: t('Please select a role.') });
      return;
    }

    setSavingEdit(true);
    setNotice(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users/${detailUser.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editFormData),
      });
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, t('Failed to update user.')));
      }

      const associationPayload =
        associationForm.profileType === 'PROVIDER'
          ? {
            profileType: 'PROVIDER',
            providerProfileId: associationForm.providerProfileId || undefined,
            providerType: associationForm.providerType || undefined,
          }
          : associationForm.profileType === 'STAFF'
            ? {
              profileType: 'STAFF',
              staffProfileId: associationForm.staffProfileId || undefined,
              staffRole: associationForm.staffRole || undefined,
            }
            : { profileType: 'NONE' };

      const associationRes = await fetch(`${API_BASE}/users/${detailUser.id}/profile-association`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(associationPayload),
      });
      if (!associationRes.ok) {
        throw new Error(await readErrorMessage(associationRes, t('Failed to update user profile association.')));
      }

      navigate('/settings/users');
      await Promise.all([loadUsers(), loadProfileDirectories()]);
      setNotice({ type: 'success', message: t('User updated successfully.') });
    } catch (err) {
      setNotice({ type: 'error', message: (err as Error).message || t('Failed to update user.') });
    } finally {
      setSavingEdit(false);
    }
  }

  async function confirmResetPassword() {
    if (!showResetDialogFor) return;
    setProcessingReset(true);
    setNotice(null);

    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users/${showResetDialogFor.id}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ reason: 'admin-triggered-reset' }),
      });
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, t('Failed to reset password.')));
      }

      setShowResetDialogFor(null);
      await loadUsers();
      setNotice({ type: 'success', message: t('Temporary password reset email sent.') });
    } catch (err) {
      setNotice({ type: 'error', message: (err as Error).message || t('Failed to reset password.') });
    } finally {
      setProcessingReset(false);
    }
  }

  async function handleDeleteUser(user: User) {
    if (!user.active || user.status === 'ARCHIVED') {
      setDeletingUserId(user.id);
      setNotice(null);
      try {
        const token = await getToken();
        const matchedRole = roles.find((role) => role.name === user.role.name);
        const res = await fetch(`${API_BASE}/users/${user.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            firstName: user.firstName,
            lastName: user.lastName,
            roleId: matchedRole?.id ?? roles[0]?.id,
            secondaryRoles: Array.isArray(user.secondaryRoles) ? user.secondaryRoles : [],
            title: user.title || '',
            phone: user.phone || '',
            active: true,
            timesheetEnabled: Boolean(user.timesheetEnabled),
          }),
        });
        if (!res.ok) {
          throw new Error(await readErrorMessage(res, t('Failed to reactivate staff member.')));
        }
        await loadUsers();
        setNotice({ type: 'success', message: t('Staff member reactivated successfully.') });
      } catch (err) {
        setNotice({ type: 'error', message: (err as Error).message || t('Failed to reactivate staff member.') });
      } finally {
        setDeletingUserId(null);
      }
      return;
    }

    if (!window.confirm(t('Archive user {name}? This will also remove their Cognito identity.').replace('{name}', `${user.firstName} ${user.lastName}`))) {
      return;
    }

    setDeletingUserId(user.id);
    setNotice(null);
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/users/${user.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        throw new Error(await readErrorMessage(res, t('Failed to archive user.')));
      }
      await loadUsers();
      setNotice({ type: 'success', message: t('User archived successfully.') });
    } catch (err) {
      setNotice({ type: 'error', message: (err as Error).message || t('Failed to archive user.') });
    } finally {
      setDeletingUserId(null);
    }
  }

  const departments = useMemo(() => Array.from(new Set(users.map((user) => user.title || user.linkedStaffRole || user.linkedProviderType || user.role.name).filter(Boolean))).sort(), [users]);
  const filtered = useMemo(
    () =>
      users.filter((u) => {
        const term = search.toLowerCase().trim();
        const matchesSearch =
          !term ||
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(term) ||
          `${u.lastName} ${u.firstName}`.toLowerCase().includes(term) ||
          u.email.toLowerCase().includes(term) ||
          (u.title || '').toLowerCase().includes(term);
        const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? u.active && u.status !== 'ARCHIVED' : !u.active || u.status === 'ARCHIVED');
        const department = u.title || u.linkedStaffRole || u.linkedProviderType || u.role.name;
        const matchesDepartment = departmentFilter === 'all' || department === departmentFilter;
        const matchesAdmin = !adminOnlyFilter || u.role.name.toUpperCase() === 'ADMIN' || (u.secondaryRoles || []).includes('ADMIN');
        const matchesLocation = locationFilter === 'all' || true;
        return matchesSearch && matchesStatus && matchesDepartment && matchesAdmin && matchesLocation;
      }),
    [adminOnlyFilter, departmentFilter, locationFilter, search, statusFilter, users],
  );
  const availableProviderProfiles = useMemo(
    () =>
      providerProfiles.filter((profile) => !profile.userId || profile.userId === detailUser?.id),
    [providerProfiles, detailUser?.id],
  );
  const availableStaffProfiles = useMemo(
    () =>
      staffProfiles.filter((profile) => !profile.userId || profile.userId === detailUser?.id),
    [staffProfiles, detailUser?.id],
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          {!userId ? (
            <button onClick={() => navigate('/settings')} className="rounded-full p-2 hover:bg-slate-100">
              <ArrowLeft size={20} />
            </button>
          ) : null}
          <div>
            <h1 className="text-2xl font-semibold text-ink">{t('Team')}</h1>
            <p className="text-slate-500 text-sm">{t('Manage staff profiles, permissions, locations, timesheets, and documents.')}</p>
          </div>
        </div>
        {isAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-ink text-white rounded-lg shadow-soft flex items-center gap-2"
          >
            <Plus size={16} /> {t('New Staff Member')}
          </button>
        )}
      </div>

      {notice && (
        <div className={`rounded-lg border px-4 py-3 text-sm flex items-start justify-between gap-3 ${
          notice.type === 'success'
            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
            : notice.type === 'error'
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-slate-50 border-slate-200 text-slate-700'
        }`}>
          <span>{notice.message}</span>
          <button type="button" onClick={() => setNotice(null)} className="opacity-70 hover:opacity-100">
            <X size={14} />
          </button>
        </div>
      )}

      {roleLoading ? (
        <div className="text-sm text-slate-500">{t('Loading users...')}</div>
      ) : !isAdmin ? (
        <div className="rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {t('Only administrators can view and manage users.')}
        </div>
      ) : !userId ? (
        <div className="space-y-4">
          <div className="card overflow-visible">
            <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 p-4">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-ink">{t('People')}</h2>
                  <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs font-semibold text-sky-700">{filtered.length}</span>
                </div>
                <p className="text-sm text-slate-500">{t('Search, filter, invite, and manage team members.')}</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex min-w-[260px] items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  <Search size={16} className="text-slate-400" />
                  <input
                    className="w-full outline-none text-sm"
                    placeholder={t('Search by name')}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowFilters((value) => !value)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-semibold ${showFilters ? 'border-sky-300 bg-sky-50 text-sky-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                  >
                    <Filter size={15} /> {t('Filter')}
                  </button>
                  {showFilters ? (
                    <div className="absolute right-0 top-11 z-20 w-72 rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
                      <p className="text-sm font-semibold text-ink">{t('Filter by')}</p>
                      <div className="mt-3 space-y-4">
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('Status')}</p>
                          <div className="space-y-2 text-sm text-slate-700">
                            <label className="flex items-center gap-2"><input type="radio" checked={statusFilter === 'active'} onChange={() => setStatusFilter('active')} />{t('Active')}</label>
                            <label className="flex items-center gap-2"><input type="radio" checked={statusFilter === 'inactive'} onChange={() => setStatusFilter('inactive')} />{t('Inactive')}</label>
                            <label className="flex items-center gap-2"><input type="radio" checked={statusFilter === 'all'} onChange={() => setStatusFilter('all')} />{t('All')}</label>
                          </div>
                        </div>
                        <SelectField
                          label={t('Department')}
                          value={departmentFilter}
                          options={[{ value: 'all', label: t('All') }, ...departments.map((department) => ({ value: department, label: department }))]}
                          onChange={setDepartmentFilter}
                        />
                        <SelectField
                          label={t('Location')}
                          value={locationFilter}
                          options={[{ value: 'all', label: t('All Locations') }, ...locations.map((location) => ({ value: location.id, label: location.name }))]}
                          onChange={setLocationFilter}
                        />
                        <label className="flex items-center gap-2 text-sm text-slate-700">
                          <input type="checkbox" checked={adminOnlyFilter} onChange={(event) => setAdminOnlyFilter(event.target.checked)} />
                          {t('Admin')}
                        </label>
                        <div className="grid grid-cols-2 gap-2">
                          <button type="button" onClick={() => { setStatusFilter('active'); setDepartmentFilter('all'); setLocationFilter('all'); setAdminOnlyFilter(false); }} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-600 hover:bg-slate-50">{t('Reset')}</button>
                          <button type="button" onClick={() => setShowFilters(false)} className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-semibold text-white hover:bg-sky-700">{t('Apply')}</button>
                        </div>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="inline-flex overflow-hidden rounded-lg border border-slate-200">
                  <button type="button" aria-label="Grid view" onClick={() => setViewMode('grid')} className={`p-2 ${viewMode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <Grid3X3 size={16} />
                  </button>
                  <button type="button" aria-label="List view" onClick={() => setViewMode('list')} className={`border-l border-slate-200 p-2 ${viewMode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-500 hover:bg-slate-50'}`}>
                    <List size={16} />
                  </button>
                </div>
                <button type="button" className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">
                  <List size={15} /> {t('Clinic List')}
                </button>
                <button type="button" className="rounded-lg border border-sky-200 px-3 py-2 text-sm font-semibold text-sky-700 hover:bg-sky-50">{t('Connect Wallet')}</button>
                <button type="button" onClick={() => { setCreateStep('details'); setShowCreateModal(true); }} className="inline-flex items-center gap-2 rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white shadow-soft hover:bg-sky-700">
                  <Plus size={16} /> {t('New Staff Member')}
                </button>
              </div>
            </div>
            <div className="flex gap-6 border-b border-slate-100 px-4 text-sm font-medium text-slate-500">
              {['People', 'Roles', 'Teams', 'Visibility'].map((tab) => (
                <button key={tab} type="button" className={`border-b-2 py-3 ${tab === 'People' ? 'border-sky-500 text-sky-700' : 'border-transparent hover:text-ink'}`}>
                  {t(tab)}
                </button>
              ))}
            </div>
            {loading ? <div className="p-4 text-sm text-slate-500">{t('Loading users...')}</div> : null}
          </div>

          {viewMode === 'grid' ? (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-5">
              {filtered.map((user) => (
                <TeamCard
                  key={user.id}
                  user={user}
                  t={t}
                  onOpen={() => navigate(`/settings/users/${user.id}`)}
                  onReset={() => setShowResetDialogFor(user)}
                  onArchive={() => void handleDeleteUser(user)}
                  deleting={deletingUserId === user.id}
                />
              ))}
            </div>
          ) : (
            <div className="card overflow-hidden">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-slate-500">
                  <tr>
                    <th className="p-4">{t('Team member')}</th>
                    <th className="p-4">{t('Department')}</th>
                    <th className="p-4">{t('Role')}</th>
                    <th className="p-4">{t('Features')}</th>
                    <th className="p-4">{t('Status')}</th>
                    <th className="p-4 text-right">{t('Actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((user) => (
                    <TeamListRow
                      key={user.id}
                      user={user}
                      t={t}
                      onOpen={() => navigate(`/settings/users/${user.id}`)}
                      onReset={() => setShowResetDialogFor(user)}
                      onArchive={() => void handleDeleteUser(user)}
                      deleting={deletingUserId === user.id}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 p-4 backdrop-blur-sm">
          <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-xl animate-in zoom-in-95">
            <div className="flex items-start justify-between border-b border-slate-100 p-6">
              <div>
                <p className="text-sm text-slate-500">{t('Team')} &gt; {t('Create')}</p>
                <h2 className="mt-1 text-xl font-bold text-ink">{t('Invite teammates')}</h2>
              </div>
              {showPrefill && (
                <button
                  type="button"
                  onClick={prefill}
                  className="px-3 py-1 text-xs border border-dashed border-slate-300 rounded-lg text-slate-600 hover:text-ink hover:border-ink"
                >
                  {t('Prefill demo data')}
                </button>
              )}
            </div>
            <form onSubmit={handleCreateSubmit}>
              {createStep === 'details' ? (
                <div className="space-y-6 p-6">
                  <div>
                    <h3 className="font-semibold text-ink">{t('Invite teammates')}</h3>
                    <p className="mt-1 text-sm text-slate-500">{t('Add a new staff member with an email address, location, and role.')}</p>
                  </div>
                  <div>
                    <p className="mb-3 text-sm font-semibold text-slate-700">{t('Where do they work?')}</p>
                    <div className="grid gap-3 md:grid-cols-3">
                      {locations.slice(0, 3).map((location) => (
                        <button
                          key={location.id}
                          type="button"
                          onClick={() => setFormData((current) => ({ ...current, defaultLocationId: location.id }))}
                          className={`rounded-lg border p-4 text-left ${formData.defaultLocationId === location.id ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-200' : 'border-slate-200 hover:bg-slate-50'}`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <MapPin size={18} className={formData.defaultLocationId === location.id ? 'text-sky-600' : 'text-slate-400'} />
                            {formData.defaultLocationId === location.id ? <CheckCircle2 size={18} className="text-sky-600" /> : null}
                          </div>
                          <p className="mt-4 text-sm font-semibold text-ink">{location.name}</p>
                          <p className="mt-1 text-sm text-slate-500">{location.addressLine1 || location.city || t('Clinic location')}</p>
                        </button>
                      ))}
                    </div>
                    <details className="mt-4">
                      <summary className="cursor-pointer text-sm font-semibold text-slate-700">{t('This team member works at other locations')}</summary>
                      <div className="mt-3 grid gap-2 md:grid-cols-2">
                        {locations.map((location) => (
                          <label key={location.id} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                            <input
                              type="checkbox"
                              checked={formData.assignedLocationIds.includes(location.id)}
                              onChange={(event) =>
                                setFormData((current) => ({
                                  ...current,
                                  assignedLocationIds: event.target.checked
                                    ? Array.from(new Set([...current.assignedLocationIds, location.id]))
                                    : current.assignedLocationIds.filter((id) => id !== location.id),
                                }))
                              }
                            />
                            {location.name}
                          </label>
                        ))}
                      </div>
                    </details>
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    <TextField label={t('First Name')} value={formData.firstName} onChange={(value) => setFormData({ ...formData, firstName: value })} />
                    <TextField label={t('Last Name')} value={formData.lastName} onChange={(value) => setFormData({ ...formData, lastName: value })} />
                    <label className="block space-y-1 text-sm md:col-span-2">
                      <span className="font-medium text-slate-600">{t('Email')}</span>
                      <input type="email" className="w-full rounded-lg border border-slate-200 px-3 py-2" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                      {formData.email.includes('@') ? <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-emerald-600">{t('Validated')} <CheckCircle2 size={12} /></span> : null}
                    </label>
                    <SelectField
                      label={t('Staff Title')}
                      value={formData.title}
                      options={[
                        { value: '', label: t('Select job title') },
                        ...['Dermatologist', 'Practitioner', 'Receptionist', 'Manager', 'Cosmetologist', 'Nurse Practitioner'].map((title) => ({ value: title, label: title })),
                      ]}
                      onChange={(value) => setFormData({ ...formData, title: value })}
                    />
                    <label className="block space-y-1 text-sm">
                      <span className="font-medium text-slate-600">{t('Initial Language')}</span>
                      <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={formData.preferredLanguage} onChange={(e) => setFormData({ ...formData, preferredLanguage: e.target.value as SupportedLanguage | '' })}>
                        <option value="">{t('Use organization default')}</option>
                        {supportedLanguages.map((language) => <option key={language} value={language}>{languageLabels[language]}</option>)}
                      </select>
                    </label>
                  </div>
                </div>
              ) : (
                <div className="space-y-6 p-6">
                  <div>
                    <h3 className="font-semibold text-ink">{t('Role')}</h3>
                    <p className="mt-1 text-sm text-slate-500">{t('Choose which user group you wish to assign to this staff member.')}</p>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    {roles.map((role) => (
                      <button
                        key={role.id}
                        type="button"
                        onClick={() => {
                          const nextPrimaryRole = role.name;
                          setFormData((current) => ({ ...current, roleId: role.id, secondaryRoles: current.secondaryRoles.filter((item) => item !== nextPrimaryRole) }));
                        }}
                        className={`min-h-28 rounded-lg border p-4 text-left ${formData.roleId === role.id ? 'border-sky-400 bg-sky-50 ring-1 ring-sky-200' : 'border-slate-200 hover:bg-slate-50'}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="inline-flex items-center gap-2 font-semibold text-ink"><ShieldCheck size={16} /> {role.name}</span>
                          {formData.roleId === role.id ? <CheckCircle2 size={18} className="text-sky-600" /> : null}
                        </div>
                        <p className="mt-3 text-sm text-slate-500">{role.name === 'ADMIN' ? t('Full admin access') : t('Role-based permissions')}</p>
                      </button>
                    ))}
                    <button type="button" onClick={() => setFormData((current) => ({ ...current, roleId: roles[0]?.id ?? current.roleId }))} className="min-h-28 rounded-lg border border-slate-200 p-4 text-left hover:bg-slate-50">
                      <span className="inline-flex items-center gap-2 font-semibold text-ink"><User size={16} /> {t('None')}</span>
                      <p className="mt-3 text-sm text-slate-500">{t('Set permissions manually after creating staff.')}</p>
                    </button>
                  </div>
                  <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
                    <input type="checkbox" className="mt-1" checked={formData.timesheetEnabled} onChange={(event) => setFormData({ ...formData, timesheetEnabled: event.target.checked })} />
                    <span>
                      <span className="block font-medium text-slate-700">{t('Enable timesheet tracking')}</span>
                      <span className="block text-xs text-slate-500">{t('Allow this user to appear in Timesheets for clock-ins, breaks, approvals, and payroll hours.')}</span>
                    </span>
                  </label>
                </div>
              )}
              <div className="flex justify-end gap-2 border-t border-slate-100 p-6">
                <button type="button" onClick={() => setShowCreateModal(false)} className="rounded-lg px-4 py-2 text-slate-600 hover:bg-slate-50">{t('Cancel')}</button>
                {createStep === 'permissions' ? (
                  <button type="button" onClick={() => setCreateStep('details')} className="rounded-lg border border-slate-200 px-4 py-2 text-slate-700 hover:bg-slate-50">{t('Back')}</button>
                ) : null}
                {createStep === 'details' ? (
                  <button type="button" onClick={() => setCreateStep('permissions')} className="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white hover:bg-sky-700">{t('Set permissions')}</button>
                ) : (
                  <button type="submit" disabled={savingCreate} className="rounded-lg bg-sky-600 px-4 py-2 font-semibold text-white disabled:opacity-60">{savingCreate ? t('Creating...') : t('Create staff')}</button>
                )}
              </div>
            </form>
          </div>
        </div>
      )}

      {userId && (
        <UserDetailPage
          user={detailUser}
          roles={roles}
          editFormData={editFormData}
          setEditFormData={setEditFormData}
          associationForm={associationForm}
          setAssociationForm={setAssociationForm}
          availableProviderProfiles={availableProviderProfiles}
          availableStaffProfiles={availableStaffProfiles}
          onCancel={() => navigate('/settings/users')}
          onSave={handleEditSubmit}
          saving={savingEdit}
          onReset={() => detailUser && setShowResetDialogFor(detailUser)}
          onArchive={() => detailUser && void handleDeleteUser(detailUser)}
          loading={!detailUser}
          detailTab={detailTab}
          setDetailTab={setDetailTab}
          openActionMenu={openActionMenu}
          setOpenActionMenu={setOpenActionMenu}
          showCustomizeFields={showCustomizeFields}
          setShowCustomizeFields={setShowCustomizeFields}
          prescriberFields={prescriberFields}
          setPrescriberFields={setPrescriberFields}
          t={t}
        />
      )}

      {showResetDialogFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-[460px] p-6">
            <h2 className="text-lg font-semibold text-ink">{t('Reset Password?')}</h2>
            <p className="text-sm text-slate-600 mt-2">
              {t('Reset password for')} <strong>{showResetDialogFor.firstName} {showResetDialogFor.lastName}</strong>? {t('A new temporary password will be emailed.')}
            </p>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" className="px-4 py-2 text-slate-600 hover:bg-slate-50 rounded-lg" onClick={() => setShowResetDialogFor(null)}>
                {t('Cancel')}
              </button>
              <button type="button" disabled={processingReset} className="px-4 py-2 bg-amber-600 text-white rounded-lg disabled:opacity-60" onClick={() => { void confirmResetPassword(); }}>
                {processingReset ? t('Resetting...') : t('Confirm Reset')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function teamMemberName(user: User) {
  return `${user.firstName} ${user.lastName}`.trim() || user.email;
}

function teamInitials(user: User) {
  const source = teamMemberName(user);
  return source
    .split(' ')
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function teamTitle(user: User) {
  return user.title || user.linkedStaffRole || user.linkedProviderType || user.role.name;
}

function TeamFeatureIcons({ user }: { user: User }) {
  return (
    <div className="mt-3 flex justify-center gap-2">
      {user.linkedProfileType ? <FeatureIcon title="Linked profile" icon={<BriefcaseBusiness size={12} />} tone="sky" /> : null}
      {user.timesheetEnabled ? <FeatureIcon title="Timesheets" icon={<Clock3 size={12} />} tone="amber" /> : null}
      {(user.role.name === 'ADMIN' || (user.secondaryRoles || []).includes('ADMIN')) ? <FeatureIcon title="Admin" icon={<ShieldCheck size={12} />} tone="emerald" /> : null}
    </div>
  );
}

function FeatureIcon({ title, icon, tone }: { title: string; icon: React.ReactNode; tone: 'sky' | 'amber' | 'emerald' }) {
  const classes = {
    sky: 'bg-sky-50 text-sky-700 border-sky-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-100',
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-100',
  };
  return (
    <span title={title} className={`inline-flex h-7 w-7 items-center justify-center rounded-full border ${classes[tone]}`}>
      {icon}
    </span>
  );
}

function TeamCard({
  user,
  t,
  onOpen,
  onReset,
  onArchive,
  deleting,
}: {
  user: User;
  t: (key: string) => string;
  onOpen: () => void;
  onReset: () => void;
  onArchive: () => void;
  deleting: boolean;
}) {
  return (
    <div className="group rounded-lg border border-slate-200 bg-white p-5 text-center shadow-sm transition hover:-translate-y-0.5 hover:shadow-md">
      <button type="button" onClick={onOpen} className="block w-full">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 text-lg font-semibold text-white">
          {teamInitials(user)}
        </div>
        <h3 className="mt-3 truncate font-semibold text-ink">{teamMemberName(user)}</h3>
        <p className="mt-1 truncate text-sm text-slate-500">{teamTitle(user) || t('Add job title')}</p>
        <TeamFeatureIcons user={user} />
        {!user.active || user.status === 'ARCHIVED' || user.mustChangePassword ? (
          <span className="mt-3 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-500">
            {user.status === 'ARCHIVED' ? t('Inactive') : user.mustChangePassword ? t('Pending') : t('Inactive')}
          </span>
        ) : null}
      </button>
      <div className="mt-4 flex justify-center gap-2 opacity-0 transition group-hover:opacity-100">
        <button type="button" onClick={onReset} className="rounded-lg border border-amber-200 p-2 text-amber-700 hover:bg-amber-50" title={t('Send reset password email')}>
          <KeyRound size={14} />
        </button>
        <button type="button" onClick={onArchive} disabled={deleting || user.status === 'ARCHIVED'} className="rounded-lg border border-rose-200 p-2 text-rose-700 hover:bg-rose-50 disabled:opacity-40" title={t('Deactivate')}>
          <Trash2 size={14} />
        </button>
      </div>
    </div>
  );
}

function TeamListRow({
  user,
  t,
  onOpen,
  onReset,
  onArchive,
  deleting,
}: {
  user: User;
  t: (key: string) => string;
  onOpen: () => void;
  onReset: () => void;
  onArchive: () => void;
  deleting: boolean;
}) {
  return (
    <tr className="cursor-pointer hover:bg-slate-50" onClick={onOpen}>
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-slate-900 text-xs font-semibold text-white">{teamInitials(user)}</div>
          <div>
            <p className="font-semibold text-ink">{teamMemberName(user)}</p>
            <p className="text-xs text-slate-500">{user.email}</p>
          </div>
        </div>
      </td>
      <td className="p-4 text-slate-600">{teamTitle(user)}</td>
      <td className="p-4">
        <div className="flex flex-wrap gap-1">
          <span className="rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">{user.role.name}</span>
          {(user.secondaryRoles || []).map((role) => (
            <span key={`${user.id}-${role}`} className="rounded-full bg-sky-50 px-2 py-1 text-xs font-medium text-sky-700">{role}</span>
          ))}
        </div>
      </td>
      <td className="p-4">
        <div className="flex gap-2">
          {user.timesheetEnabled ? <FeatureIcon title="Timesheets" icon={<Clock3 size={12} />} tone="amber" /> : null}
          {user.linkedProfileType ? <FeatureIcon title="Linked profile" icon={<BriefcaseBusiness size={12} />} tone="sky" /> : null}
          {(user.role.name === 'ADMIN' || (user.secondaryRoles || []).includes('ADMIN')) ? <FeatureIcon title="Admin" icon={<ShieldCheck size={12} />} tone="emerald" /> : null}
        </div>
      </td>
      <td className="p-4">
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${user.active && user.status !== 'ARCHIVED' ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
          {user.status === 'ARCHIVED' ? t('Inactive') : user.mustChangePassword ? t('Pending') : user.active ? t('Active') : t('Inactive')}
        </span>
      </td>
      <td className="p-4 text-right">
        <div className="flex justify-end gap-2" onClick={(event) => event.stopPropagation()}>
          <button type="button" onClick={onReset} className="rounded-lg p-2 text-slate-400 hover:bg-amber-50 hover:text-amber-700" title={t('Send reset password email')}>
            <KeyRound size={16} />
          </button>
          <button type="button" onClick={onArchive} disabled={deleting || user.status === 'ARCHIVED'} className="rounded-lg p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40" title={t('Deactivate')}>
            <Trash2 size={16} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ReadinessItem({ ready, label }: { ready: boolean; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-lg bg-white/70 px-3 py-2">
      <span className={`h-2.5 w-2.5 rounded-full ${ready ? 'bg-emerald-500' : 'bg-slate-300'}`} />
      <span className={ready ? 'text-slate-700' : 'text-slate-500'}>{label}</span>
    </div>
  );
}

function StaffFeatureCard({ icon, title, detail }: { icon: React.ReactNode; title: string; detail: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
      <div className="flex items-center gap-2 text-sm font-semibold text-ink">
        <span className="text-sky-600">{icon}</span>
        {title}
      </div>
      <p className="mt-2 text-xs leading-5 text-slate-500">{detail}</p>
    </div>
  );
}

function UserDetailPage({
  user,
  roles,
  editFormData,
  setEditFormData,
  associationForm,
  setAssociationForm,
  availableProviderProfiles,
  availableStaffProfiles,
  onCancel,
  onSave,
  saving,
  onReset,
  onArchive,
  loading,
  detailTab,
  setDetailTab,
  openActionMenu,
  setOpenActionMenu,
  showCustomizeFields,
  setShowCustomizeFields,
  prescriberFields,
  setPrescriberFields,
  t,
}: {
  user: User | null;
  roles: Role[];
  editFormData: {
    firstName: string;
    lastName: string;
    roleId: number;
    secondaryRoles: string[];
    title: string;
    phone: string;
    active: boolean;
    timesheetEnabled: boolean;
  };
  setEditFormData: React.Dispatch<React.SetStateAction<{
    firstName: string;
    lastName: string;
    roleId: number;
    secondaryRoles: string[];
    title: string;
    phone: string;
    active: boolean;
    timesheetEnabled: boolean;
  }>>;
  associationForm: {
    profileType: 'NONE' | 'PROVIDER' | 'STAFF';
    providerProfileId: string;
    staffProfileId: string;
    providerType: string;
    staffRole: string;
  };
  setAssociationForm: React.Dispatch<React.SetStateAction<{
    profileType: 'NONE' | 'PROVIDER' | 'STAFF';
    providerProfileId: string;
    staffProfileId: string;
    providerType: string;
    staffRole: string;
  }>>;
  availableProviderProfiles: ProviderProfileOption[];
  availableStaffProfiles: StaffProfileOption[];
  onCancel: () => void;
  onSave: (event: React.FormEvent) => void;
  saving: boolean;
  onReset: () => void;
  onArchive: () => void;
  loading: boolean;
  detailTab: TeamTab;
  setDetailTab: React.Dispatch<React.SetStateAction<TeamTab>>;
  openActionMenu: boolean;
  setOpenActionMenu: React.Dispatch<React.SetStateAction<boolean>>;
  showCustomizeFields: boolean;
  setShowCustomizeFields: React.Dispatch<React.SetStateAction<boolean>>;
  prescriberFields: {
    prescriptionInfo: string;
    registrationNumber: string;
    dea: string;
    npi: string;
    cdPin: string;
    qCode: string;
  };
  setPrescriberFields: React.Dispatch<React.SetStateAction<{
    prescriptionInfo: string;
    registrationNumber: string;
    dea: string;
    npi: string;
    cdPin: string;
    qCode: string;
  }>>;
  t: (key: string) => string;
}) {
  const selectedPrimaryRole = roles.find((role) => role.id === editFormData.roleId)?.name || '';

  if (loading || !user) {
    return (
      <div className="card p-6">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 size={16} className="animate-spin" /> {t('Loading user details...')}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-5">
        <div className="flex items-start gap-4">
          <button onClick={onCancel} className="rounded-full p-2 hover:bg-slate-100">
            <ArrowLeft size={20} />
          </button>
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-900 text-lg font-semibold text-white">
            {teamInitials(user)}
          </div>
          <div>
            <p className="text-sm text-slate-500">{t('People')} &gt; {teamMemberName(user)}</p>
            <h1 className="text-2xl font-semibold text-ink">{`${user.firstName} ${user.lastName}`.trim()}</h1>
            <p className="text-sm text-slate-500">{teamTitle(user)}</p>
            <div className="mt-2 flex flex-wrap gap-2 text-xs font-medium">
              <span className={`rounded-full px-2 py-1 ${user.active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                {user.active ? t('Active') : t('Inactive')}
              </span>
              <span className="rounded-full bg-slate-100 px-2 py-1 text-slate-600">{user.role.name}</span>
              {user.timesheetEnabled ? <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-700">{t('Timesheets & Wages')}</span> : null}
              {(user.secondaryRoles || []).map((role) => (
                <span key={`${user.id}-${role}`} className="rounded-full bg-sky-50 px-2 py-1 text-sky-700">
                  {role}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <button type="button" onClick={() => setOpenActionMenu((value) => !value)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:bg-slate-50" aria-label={t('More options')}>
              <MoreVertical size={18} />
            </button>
            {openActionMenu ? (
              <div className="absolute right-0 top-12 z-20 w-64 rounded-lg border border-slate-200 bg-white py-2 shadow-xl">
                <button type="button" onClick={() => { setOpenActionMenu(false); onReset(); }} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50">
                  <KeyRound size={15} /> {t('Send reset password email')}
                </button>
                <button type="button" className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-slate-600 hover:bg-slate-50">
                  <ShieldCheck size={15} /> {user.role.name === 'ADMIN' ? t('Revoke Admin') : t('Grant Admin')}
                </button>
                <button type="button" onClick={() => { setOpenActionMenu(false); onArchive(); }} className="flex w-full items-center gap-2 px-4 py-2 text-left text-sm text-rose-600 hover:bg-rose-50">
                  <Trash2 size={15} /> {user.active && user.status !== 'ARCHIVED' ? t('Deactivate') : t('Reactivate')}
                </button>
              </div>
            ) : null}
          </div>
          <button type="button" onClick={() => setShowCustomizeFields(true)} className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
            {t('Customize Fields')}
          </button>
          <button type="submit" form="team-member-form" disabled={saving} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">
            {saving ? t('Saving...') : t('Save Changes')}
          </button>
        </div>
      </div>

      <form id="team-member-form" onSubmit={onSave} className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)_280px]">
        <aside className="card overflow-hidden self-start">
          {[
            ['personal', 'Personal Details', User],
            ['services', 'Services', BriefcaseBusiness],
            ['permissions', 'Permissions', ShieldCheck],
            ['locations', 'Locations', MapPin],
            ['timesheets', 'Timesheets & Wages', Clock3],
            ['leave', 'Leave', CalendarDays],
            ['documents', 'Documents', FileText],
          ].map(([value, label, Icon]) => {
            const TabIcon = Icon as typeof User;
            return (
              <button
                key={String(value)}
                type="button"
                onClick={() => setDetailTab(value as TeamTab)}
                className={`flex w-full items-center gap-2 border-l-2 px-4 py-3 text-left text-sm font-medium ${detailTab === value ? 'border-sky-500 bg-sky-50 text-sky-700' : 'border-transparent text-slate-600 hover:bg-slate-50'}`}
              >
                <TabIcon size={15} /> {t(String(label))}
              </button>
            );
          })}
        </aside>

        <div className="space-y-6">
        {detailTab === 'personal' ? (
        <>
        <section className="card space-y-4 p-5">
          <div>
            <h2 className="text-lg font-semibold text-ink">{t('Personal Details')}</h2>
            <p className="text-sm text-slate-500">{t('Update the staff profile, prescription info, signature, and displayed employee fields.')}</p>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            <StaffFeatureCard icon={<Camera size={16} />} title={t('Staff photo')} detail={t('Upload, crop, preview, or remove the profile photo used across Team and online booking.')} />
            <StaffFeatureCard icon={<User size={16} />} title={t('Bio and public preview')} detail={t('Edit public biography and preview how the staff profile appears in online bookings.')} />
            <StaffFeatureCard icon={<PenLine size={16} />} title={t('Signature pad')} detail={t('Capture signature with erase, undo, redo, and quick-sign medical form support.')} />
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <TextField
              label={t('First Name')}
              value={editFormData.firstName}
              onChange={(value) => setEditFormData((current) => ({ ...current, firstName: value }))}
            />
            <TextField
              label={t('Last Name')}
              value={editFormData.lastName}
              onChange={(value) => setEditFormData((current) => ({ ...current, lastName: value }))}
            />
            <TextField label={t('Email')} value={user.email} readOnly />
            <SelectField
              label={t('Role')}
              value={String(editFormData.roleId || '')}
              options={roles.map((role) => ({ value: String(role.id), label: role.name }))}
              onChange={(value) => {
                const nextRoleId = Number(value);
                const nextPrimaryRole = roles.find((role) => role.id === nextRoleId)?.name || '';
                setEditFormData((current) => ({
                  ...current,
                  roleId: nextRoleId,
                  secondaryRoles: current.secondaryRoles.filter((role) => role !== nextPrimaryRole),
                }));
              }}
            />
            <TextField
              label={t('Title')}
              value={editFormData.title}
              onChange={(value) => setEditFormData((current) => ({ ...current, title: value }))}
            />
            <TextField
              label={t('Phone')}
              value={editFormData.phone}
              onChange={(value) => setEditFormData((current) => ({ ...current, phone: value }))}
            />
            <label className="block space-y-1 text-sm md:col-span-2">
              <span className="font-medium text-slate-600">{t('Prescription Info')}</span>
              <textarea
                className="min-h-24 w-full rounded-lg border border-slate-200 bg-white px-3 py-2"
                value={prescriberFields.prescriptionInfo}
                onChange={(event) => setPrescriberFields((current) => ({ ...current, prescriptionInfo: event.target.value }))}
                placeholder={t('Credentials shown at the bottom of prescriptions')}
              />
            </label>
            <TextField
              label={t('Registration number')}
              value={prescriberFields.registrationNumber}
              onChange={(value) => setPrescriberFields((current) => ({ ...current, registrationNumber: value }))}
            />
            <TextField
              label={t('DEA')}
              value={prescriberFields.dea}
              onChange={(value) => setPrescriberFields((current) => ({ ...current, dea: value }))}
            />
            <TextField
              label={t('NPI')}
              value={prescriberFields.npi}
              onChange={(value) => setPrescriberFields((current) => ({ ...current, npi: value }))}
            />
            <TextField
              label={t('CD PIN')}
              value={prescriberFields.cdPin}
              onChange={(value) => setPrescriberFields((current) => ({ ...current, cdPin: value }))}
            />
            <TextField
              label={t('Q Code')}
              value={prescriberFields.qCode}
              onChange={(value) => setPrescriberFields((current) => ({ ...current, qCode: value }))}
            />
            <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <input
                type="checkbox"
                checked={editFormData.active}
                onChange={(event) => setEditFormData((current) => ({ ...current, active: event.target.checked }))}
              />
              <span className="text-sm text-slate-700">{t('Active user')}</span>
            </div>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
              <input
                type="checkbox"
                className="mt-1"
                checked={editFormData.timesheetEnabled}
                onChange={(event) => setEditFormData((current) => ({ ...current, timesheetEnabled: event.target.checked }))}
              />
              <span>
                <span className="block text-sm font-medium text-slate-700">{t('Has timesheet')}</span>
                <span className="block text-xs text-slate-500">{t('Include this user in the Timesheets module and payroll hour tracking.')}</span>
              </span>
            </label>
          </div>
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 text-sky-700" size={18} />
              <div>
                <h3 className="font-semibold text-slate-950">{t('Controlled Drug & FP10 readiness')}</h3>
                <p className="mt-1 text-sm text-slate-600">
                  {t('CD PIN and Q Code identify authorised prescribers for Schedule 2 and 3 controlled drug FP10 printing and NHS reimbursement routing.')}
                </p>
                <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
                  <ReadinessItem ready={Boolean(prescriberFields.registrationNumber)} label={t('GMC/GPhC/GDC registration recorded')} />
                  <ReadinessItem ready={Boolean(prescriberFields.cdPin)} label={t('CD PIN added')} />
                  <ReadinessItem ready={Boolean(prescriberFields.qCode)} label={t('Q Code added')} />
                  <ReadinessItem ready={Boolean(prescriberFields.prescriptionInfo)} label={t('Prescription footer credentials ready')} />
                </div>
              </div>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="font-semibold text-ink">{t('FP10 printing modes')}</h3>
              <div className="mt-3 space-y-3 text-sm text-slate-600">
                <p><strong>{t('Blank FP10 pads')}:</strong> {t('Clinic and clinician details print automatically with patient and medication information.')}</p>
                <p><strong>{t('Pre-printed FP10 pads')}:</strong> {t('Only patient and medication information is printed.')}</p>
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 p-4">
              <h3 className="font-semibold text-ink">{t('Controlled drug compliance')}</h3>
              <div className="mt-3 space-y-2 text-sm text-slate-600">
                <ReadinessItem ready={Boolean(prescriberFields.cdPin && prescriberFields.qCode)} label={t('Only controlled drugs can be issued via FP10 workflow')} />
                <ReadinessItem ready={Boolean(prescriberFields.registrationNumber)} label={t('Generic name, form, strength, dose, and quantity rules enforced')} />
                <ReadinessItem ready={Boolean(prescriberFields.prescriptionInfo)} label={t('Controlled drug prescriptions marked in client records')} />
              </div>
            </div>
          </div>
          <div>
            <p className="mb-2 text-sm font-medium text-slate-700">{t('Additional Roles')}</p>
            <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {roles
                .map((role) => role.name)
                .filter((roleName) => roleName !== selectedPrimaryRole)
                .map((roleName) => (
                  <label key={roleName} className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={editFormData.secondaryRoles.includes(roleName)}
                      onChange={(event) =>
                        setEditFormData((current) => ({
                          ...current,
                          secondaryRoles: event.target.checked
                            ? Array.from(new Set([...current.secondaryRoles, roleName]))
                            : current.secondaryRoles.filter((entry) => entry !== roleName),
                        }))
                      }
                    />
                    <span>{roleName}</span>
                  </label>
                ))}
            </div>
          </div>
        </section>

        <section className="card space-y-4 p-5">
          <div>
            <h2 className="text-lg font-semibold text-ink">{t('Profile Association')}</h2>
            <p className="text-sm text-slate-500">{t('Link this user to a provider or staff profile for clinical and operational workflows.')}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <SelectField
              label={t('Linked As')}
              value={associationForm.profileType}
              options={[
                { value: 'NONE', label: t('Not linked') },
                { value: 'PROVIDER', label: t('Provider') },
                { value: 'STAFF', label: t('Staff') },
              ]}
              onChange={(value) => setAssociationForm((current) => ({ ...current, profileType: value as 'NONE' | 'PROVIDER' | 'STAFF' }))}
            />
            {associationForm.profileType === 'PROVIDER' ? (
              <SelectField
                label={t('Provider Profile')}
                value={associationForm.providerProfileId}
                options={[
                  { value: '', label: t('Auto-create or use linked provider') },
                  ...availableProviderProfiles.map((profile) => ({
                    value: profile.id,
                    label: `${profile.lastName}, ${profile.firstName} (${profile.type})`,
                  })),
                ]}
                onChange={(value) => setAssociationForm((current) => ({ ...current, providerProfileId: value }))}
              />
            ) : null}
            {associationForm.profileType === 'PROVIDER' ? (
              <TextField
                label={t('Provider Type (optional override)')}
                value={associationForm.providerType}
                onChange={(value) => setAssociationForm((current) => ({ ...current, providerType: value }))}
              />
            ) : null}
            {associationForm.profileType === 'STAFF' ? (
              <SelectField
                label={t('Staff Profile')}
                value={associationForm.staffProfileId}
                options={[
                  { value: '', label: t('Auto-create or use linked staff') },
                  ...availableStaffProfiles.map((profile) => ({
                    value: profile.id,
                    label: `${profile.lastName}, ${profile.firstName} (${profile.role})`,
                  })),
                ]}
                onChange={(value) => setAssociationForm((current) => ({ ...current, staffProfileId: value }))}
              />
            ) : null}
            {associationForm.profileType === 'STAFF' ? (
              <TextField
                label={t('Staff Role (optional override)')}
                value={associationForm.staffRole}
                onChange={(value) => setAssociationForm((current) => ({ ...current, staffRole: value }))}
              />
            ) : null}
          </div>
        </section>
        </>
        ) : detailTab === 'permissions' ? (
          <section className="card space-y-4 p-5">
            <div>
              <h2 className="text-lg font-semibold text-ink">{t('Permissions')}</h2>
              <p className="text-sm text-slate-500">{t('Assign a role or fine-tune access by feature area.')}</p>
            </div>
            <SelectField
              label={t('Assigned Role')}
              value={String(editFormData.roleId || '')}
              options={roles.map((role) => ({ value: String(role.id), label: role.name }))}
              onChange={(value) => setEditFormData((current) => ({ ...current, roleId: Number(value) }))}
            />
            <div className="grid gap-3 md:grid-cols-2">
              {['Calendar', 'Clients', 'Clinical', 'Billing', 'Marketing', 'Reports', 'Setup', 'Team'].map((group) => (
                <div key={group} className="rounded-lg border border-slate-200 p-4">
                  <div className="flex items-center justify-between">
                    <p className="font-semibold text-ink">{t(group)}</p>
                    <button type="button" className="text-xs font-semibold text-sky-700">{t('Check All')}</button>
                  </div>
                  <div className="mt-3 space-y-2 text-sm text-slate-600">
                    {['View', 'Create', 'Edit'].map((permission) => (
                      <label key={`${group}-${permission}`} className="flex items-center justify-between gap-2">
                        <span>{t(permission)} {t(group)}</span>
                        <input type="checkbox" defaultChecked />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <StaffFeatureCard icon={<BadgePercent size={16} />} title={t('POS discount permissions')} detail={t('Control whether this staff member can apply discounts, override prices, and approve service charges.')} />
              <StaffFeatureCard icon={<FileText size={16} />} title={t('Credit-note permissions')} detail={t('Allow or block credit note creation, refund approval, and client account transfers.')} />
              <StaffFeatureCard icon={<Bell size={16} />} title={t('Staff notifications')} detail={t('Configure appointment, lead, form-complete, and client-review alerts.')} />
              <StaffFeatureCard icon={<Smartphone size={16} />} title={t('iOS permissions')} detail={t('Track camera, photo, microphone, notifications, Bluetooth, and location permissions for Pabau Go.')} />
            </div>
          </section>
        ) : detailTab === 'locations' ? (
          <section className="card space-y-4 p-5">
            <h2 className="text-lg font-semibold text-ink">{t('Locations')}</h2>
            <div className="rounded-lg border border-sky-100 bg-sky-50 p-3 text-sm text-sky-800">
              {t('Primary and allowed locations are saved to the staff profile and control team filters, bookings, timesheets, and reports.')}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {fallbackLocations.map((location) => (
                <label key={location.id} className="flex items-start gap-3 rounded-lg border border-slate-200 p-4">
                  <input type="checkbox" defaultChecked={location.isPrimary} className="mt-1" />
                  <span>
                    <span className="block font-semibold text-ink">{location.name}</span>
                    <span className="block text-sm text-slate-500">{location.addressLine1}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>
        ) : detailTab === 'timesheets' ? (
          <section className="card space-y-4 p-5">
            <h2 className="text-lg font-semibold text-ink">{t('Timesheets & Wages')}</h2>
            <label className="flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3">
              <input type="checkbox" className="mt-1" checked={editFormData.timesheetEnabled} onChange={(event) => setEditFormData((current) => ({ ...current, timesheetEnabled: event.target.checked }))} />
              <span>
                <span className="block font-semibold text-ink">{t('Timesheets & Wages')}</span>
                <span className="block text-sm text-slate-500">{t('Enable clock-in, breaks, clock-out, overtime, and wage calculations for this staff member.')}</span>
              </span>
            </label>
            <div className="grid gap-4 md:grid-cols-2">
              <TextField label={t('Compensation type')} value={t('Hourly pay')} readOnly />
              <TextField label={t('Rate')} value="$24.00" readOnly />
              <TextField label={t('Regular working hours')} value="40 per week" readOnly />
              <TextField label={t('Overtime pay')} value="1.5x multiplier" readOnly />
            </div>
          </section>
        ) : detailTab === 'documents' ? (
          <section className="card space-y-4 p-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-ink">{t('Documents')}</h2>
                <p className="text-sm text-slate-500">{t('Upload certificates, contracts, and staff records.')}</p>
              </div>
              <button type="button" className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">{t('Upload')}</button>
            </div>
            <button type="button" className="rounded-lg border border-dashed border-slate-300 p-6 text-left text-sm text-slate-500 hover:bg-slate-50">
              <FileText className="mb-2 text-slate-400" size={20} />
              {t('Create Folder')} · {t('Certificates, contracts, licenses')}
            </button>
            <div className="grid gap-3 md:grid-cols-2">
              <StaffFeatureCard icon={<UploadCloud size={16} />} title={t('Document upload flow')} detail={t('Create shared folders, upload files, choose folder, name document, and save staff records.')} />
              <StaffFeatureCard icon={<FileText size={16} />} title={t('Expiry and acknowledgement')} detail={t('Track document expiry, staff acknowledgement, sharing status, and future reminders.')} />
            </div>
          </section>
        ) : detailTab === 'services' ? (
          <section className="card space-y-4 p-5">
            <h2 className="text-lg font-semibold text-ink">{t('Services')}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {['Offer services', 'Sell products', 'Appear on calendar', 'Accept online bookings'].map((item) => (
                <label key={item} className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 text-sm text-slate-700">
                  <span>{t(item)}</span>
                  <input type="checkbox" defaultChecked />
                </label>
              ))}
            </div>
            <div className="grid gap-3 md:grid-cols-3">
              <StaffFeatureCard icon={<Clock3 size={16} />} title={t('Duration overrides')} detail={t('Set employee-specific service duration, buffer, and extra time rules.')} />
              <StaffFeatureCard icon={<BadgePercent size={16} />} title={t('Pricing overrides')} detail={t('Set staff-specific service prices, package redemption rules, and commission defaults.')} />
              <StaffFeatureCard icon={<Link2 size={16} />} title={t('Booking links')} detail={t('Generate staff-specific online booking links for selected services and locations.')} />
            </div>
          </section>
        ) : (
          <section className="card space-y-4 p-5">
            <h2 className="text-lg font-semibold text-ink">{t(detailTab === 'leave' ? 'Leave' : 'Team')}</h2>
            <div className="grid gap-3 md:grid-cols-2">
              <StaffFeatureCard icon={<CalendarDays size={16} />} title={t('Leave requests')} detail={t('View booked leave and submit leave requests on behalf of this team member.')} />
              <StaffFeatureCard icon={<Bell size={16} />} title={t('Approval notifications')} detail={t('Notify admins and staff when leave is requested, approved, or declined.')} />
            </div>
          </section>
        )}
        </div>

        <aside className="hidden space-y-4 lg:block">
          <div className="card p-4">
            <h3 className="font-semibold text-ink">{t('Pabau Go')}</h3>
            <p className="mt-2 text-sm text-slate-500">{t('Use passcodes for calendar locks and app access.')}</p>
            <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800">
              <p className="font-semibold">{t('Expert Advice')}</p>
              <p className="mt-1">{t('Avoid repeated digits or sequences for staff passcodes.')}</p>
            </div>
            <TextField label={t('Passcode')} value="••••" readOnly />
          </div>
          <div className="card p-4">
            <h3 className="font-semibold text-ink">{t('Online booking preview')}</h3>
            <p className="mt-2 text-sm text-slate-500">{t('Bio and services appear in the patient booking portal.')}</p>
            <button type="button" className="mt-3 rounded-lg border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">{t('Preview profile')}</button>
          </div>
          <div className="card p-4">
            <h3 className="font-semibold text-ink">{t('Controlled drug best practice')}</h3>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              <li>{t('Restrict FP10 access to authorised prescribers only.')}</li>
              <li>{t('Use secure FP10 printer trays and stationery storage.')}</li>
              <li>{t('Controlled drug quantities should generally not exceed a 30-day supply.')}</li>
              <li>{t('Run monthly audit reports for compliance.')}</li>
            </ul>
          </div>
        </aside>
      </form>
      {showCustomizeFields ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
          <div className="max-h-[86vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="flex items-start justify-between border-b border-slate-100 p-6">
              <div>
                <h2 className="text-xl font-semibold text-ink">{t('Customize fields for all employees')}</h2>
                <p className="text-sm text-slate-500">{t('Drag to reorder information on the Personal Tab')}</p>
              </div>
              <button type="button" onClick={() => setShowCustomizeFields(false)} className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"><X size={20} /></button>
            </div>
            <div className="space-y-2 p-6">
              {['First name', 'Last name', 'Email', 'Prescription Info', 'Registration number', 'DEA', 'NPI', 'CD PIN', 'Q Code', 'Staff Title', 'Bio', 'Birthday date', 'Cell phone', 'Department'].map((field) => (
                <div key={field} className="flex items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-600">
                  <List size={15} /> {t(field)}
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t border-slate-100 p-4">
              <button type="button" onClick={() => setShowCustomizeFields(false)} className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white">{t('Save')}</button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  readOnly,
}: {
  label: string;
  value: string;
  onChange?: (value: string) => void;
  readOnly?: boolean;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <input
        className={`w-full rounded-lg border px-3 py-2 ${readOnly ? 'bg-slate-100' : 'bg-white'} border-slate-200`}
        value={value}
        readOnly={readOnly}
        onChange={(event) => onChange?.(event.target.value)}
      />
    </label>
  );
}

function SelectField({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <label className="block space-y-1 text-sm">
      <span className="font-medium text-slate-600">{label}</span>
      <select className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2" value={value} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}
